import {
    computed,
    defineComponent,
    ref,
} from "vue";
import {
    PublicTrustResponseSchema,
    PublicTrustServerSchema,
    getPublicServerCanonicalReference,
    type PublicTrustResponse,
    type PublicTrustServer,
    type PublicTrustTestimonial,
} from "@logscord/public-server";
import {
    PublicFooter,
    PublicNavbar,
    usePublicLocalizedPath,
} from "./PublicShell.js";
import { useLocale } from "../../locales/i18n.js";
import { usePublicReveal } from "./usePublicReveal.js";
import { useVuePublicRouteData } from "@logscord/public-site/adapters/vue";
import {
    PublicServerAvatar,
    PublicServerBadgeList,
} from "./serverDirectoryShared.js";

const EmptyTrustResponse: PublicTrustResponse = {
    stats: {
        serverCount: 0,
        totalMembers: 0,
        totalOnline: 0,
        discordVerifiedCount: 0,
        discordPartneredCount: 0,
    },
    servers: [],
    testimonials: [],
};

function isPublicTrustResponse(value: unknown): value is PublicTrustResponse {
    return PublicTrustResponseSchema.safeParse(value).success;
}

function isPublicTrustServer(value: unknown): value is PublicTrustServer {
    return PublicTrustServerSchema.safeParse(value).success;
}

function formatPublicNumber(value: number, locale: string) {
    return new Intl.NumberFormat(locale, {
        notation             : "compact",
        maximumFractionDigits: 1,
    }).format(value);
}

function getInitials(name: string) {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? "")
        .join("");
}

const TrustServerCard = defineComponent({
    props: {
        server: {
            type: Object as () => PublicTrustServer,
            required: true,
        },
        rank: {
            type: Number,
            required: true,
        },
    },

    setup(props) {
        const { i18n, locale } = useLocale();
        const publicPath = usePublicLocalizedPath();

        return () => (
            <router-link
                class="trust-server-card"
                data-reveal="server-card"
                to={publicPath(`/servers/${getPublicServerCanonicalReference(props.server)}`)}
            >
                <div class="trust-server-card-header">
                    <div class="trust-server-rank">#{props.rank}</div>
                    <PublicServerAvatar server={props.server} className="trust-server-avatar" />

                    <div class="trust-server-content">
                        <div class="trust-server-title">
                            <h3>{props.server.name}</h3>
                            <PublicServerBadgeList badges={props.server.badges} compact />
                        </div>

                        <div class="trust-server-voice">
                            <i class="material-symbols-outlined">settings_voice</i>
                            <span>{formatPublicNumber(props.server.currentVoiceCount, locale.value)} {i18n("trust.voice")}</span>
                        </div>
                    </div>
                </div>

                <div class="trust-server-card-metrics">
                    <div class="trust-server-metric">
                        <strong>{formatPublicNumber(props.server.memberCount, locale.value)}</strong>
                        <span class="trust-server-metric-label">
                            <span class="trust-server-metric-chip trust-server-metric-chip-members" />
                            <span>{i18n("trust.members")}</span>
                        </span>
                    </div>

                    <div class="trust-server-metric trust-server-metric-online">
                        {props.server.approximateOnlineCount !== null
                            ? (
                                <>
                                    <strong>{formatPublicNumber(props.server.approximateOnlineCount, locale.value)}</strong>
                                    <span class="trust-server-metric-label">
                                        <span class="trust-server-metric-chip trust-server-metric-chip-online" />
                                        <span>{i18n("trust.online")}</span>
                                    </span>
                                </>
                            )
                            : (
                                <>
                                    <strong>—</strong>
                                    <span class="trust-server-metric-label">
                                        <span class="trust-server-metric-chip trust-server-metric-chip-online" />
                                        <span>{i18n("trust.online")}</span>
                                    </span>
                                </>
                            )}
                    </div>
                </div>
            </router-link>
        );
    },
});

const TrustTestimonialCard = defineComponent({
    props: {
        testimonial: {
            type: Object as () => PublicTrustTestimonial,
            required: true,
        },
        server: {
            type: Object as () => PublicTrustServer | null,
            default: null,
        },
    },

    setup(props) {
        return () => (
            <div class="trust-testimonial" data-reveal="testimonial">
                <p>“{props.testimonial.quote}”</p>

                <div class="trust-testimonial-author">
                    <div class="trust-testimonial-avatar">
                        {props.testimonial.avatarUrl
                            ? <img src={props.testimonial.avatarUrl} alt="" loading="lazy" />
                            : <span>{getInitials(props.testimonial.authorName)}</span>}
                    </div>

                    <div>
                        <strong>{props.testimonial.authorName}</strong>
                        {props.testimonial.authorRole && <span>{props.testimonial.authorRole}</span>}
                        {props.server && <small>{props.server.name}</small>}
                    </div>
                </div>

                <PublicServerBadgeList badges={props.testimonial.badges} />
            </div>
        );
    },
});

export default defineComponent({
    setup() {
        const { i18n, locale } = useLocale();
        const publicRouteData = useVuePublicRouteData();
        const publicPath = usePublicLocalizedPath();
        const pageElement = ref<HTMLElement | null>(null);
        const publicTrust = computed(() => {
            const value = publicRouteData.getData("publicTrust");

            return isPublicTrustResponse(value)
                ? value
                : null;
        });
        const data = computed<PublicTrustResponse>(() => (
            publicTrust.value ?? EmptyTrustResponse
        ));
        const loading = computed(() => (
            publicRouteData.state.value?.status === "loading"
            || publicRouteData.state.value?.status === "idle"
        ));
        const failed = computed(() => (
            publicRouteData.state.value?.status === "unavailable"
            || publicRouteData.state.value?.status === "not-found"
        ));

        usePublicReveal(pageElement);

        const serverById = computed(() => new Map(data.value.servers.map((server) => [ server.guildId, server ])));

        return () => (
            <div class="landing-page trust-page" ref={pageElement}>
                <PublicNavbar />

                <div class="trust-hero" data-reveal="hero-copy">
                    <div>
                        <span class="landing-kicker">{i18n("trust.hero.kicker")}</span>
                        <h1>{i18n("trust.hero.title")}</h1>
                        <p>{i18n("trust.hero.description")}</p>
                    </div>
                </div>

                <div class="trust-stats" data-reveal="section-heading">
                    <div class="trust-stat-item">
                        <i class="material-symbols-outlined trust-stat-icon">groups</i>
                        <div class="trust-stat-content">
                            <strong>{formatPublicNumber(data.value.stats.totalMembers, locale.value)}</strong>
                            <span>{i18n("trust.stats.members")}</span>
                        </div>
                    </div>
                    <div class="trust-stat-item">
                        <i class="material-symbols-outlined trust-stat-icon">radio_button_checked</i>
                        <div class="trust-stat-content">
                            <strong>{formatPublicNumber(data.value.stats.totalOnline, locale.value)}</strong>
                            <span>{i18n("trust.stats.online")}</span>
                        </div>
                    </div>
                    <div class="trust-stat-item">
                        <i class="material-symbols-outlined trust-stat-icon">dns</i>
                        <div class="trust-stat-content">
                            <strong>{formatPublicNumber(data.value.stats.serverCount, locale.value)}</strong>
                            <span>{i18n("trust.stats.servers")}</span>
                        </div>
                    </div>
                </div>

                {failed.value && (
                    <div class="trust-error">
                        <i class="material-symbols-outlined">cloud_off</i>
                        {i18n("trust.error")}
                    </div>
                )}

                <div class="trust-section-heading" data-reveal="section-heading">
                    <i class="material-symbols-outlined">workspace_premium</i>
                    <div>
                        <span class="landing-kicker">{i18n("trust.top.kicker")}</span>
                        <h2>{i18n("trust.top.title")}</h2>
                        <p>{i18n("trust.top.description")}</p>
                    </div>
                </div>

                <div class="trust-server-grid">
                    {(loading.value ? Array.from({ length: 6 }) : data.value.servers).map((server, index) => (
                        isPublicTrustServer(server)
                            ? <TrustServerCard server={server} rank={index + 1} />
                            : <div class="trust-server-card trust-skeleton" />
                    ))}
                </div>

                <div class="trust-ranking-section" data-reveal="section-heading">
                    <div class="trust-section-heading">
                        <i class="material-symbols-outlined">leaderboard</i>
                        <div>
                            <span class="landing-kicker">{i18n("trust.ranking.kicker")}</span>
                            <h2>{i18n("trust.ranking.title")}</h2>
                            <p>{i18n("trust.ranking.description")}</p>
                        </div>
                    </div>

                    <div
                        class="trust-ranking"
                        data-reveal="ranking"
                        data-reveal-threshold="0"
                        data-reveal-root-margin="0px"
                    >
                        <div class="trust-ranking-header" role="row">
                            <span class="trust-ranking-rank-column" role="columnheader">#</span>
                            <span class="trust-ranking-name-header" role="columnheader">{i18n("trust.ranking.columns.name")}</span>
                            <span class="trust-ranking-metric-tracking" role="columnheader">
                                <span>{i18n("trust.ranking.columns.members")}</span>
                            </span>
                            <span class="trust-ranking-metric-tracking" role="columnheader">
                                <span>{i18n("trust.ranking.columns.online")}</span>
                            </span>
                            <span class="trust-ranking-metric-tracking" role="columnheader">
                                <span>{i18n("trust.ranking.columns.voice")}</span>
                            </span>
                        </div>
                        {data.value.servers.slice(0, 10).map((server, index) => (
                            <router-link
                                class="ranking-row"
                                role="row"
                                to={publicPath(`/servers/${getPublicServerCanonicalReference(server)}`)}
                            >
                                <span class="trust-ranking-rank-column">{index + 1}</span>
                                <div class="trust-ranking-name-cell">
                                    <PublicServerAvatar server={server} className="trust-server-avatar" />
                                    <div class="trust-ranking-name">
                                        <strong>{server.name}</strong>
                                        <PublicServerBadgeList badges={server.badges.slice(0, 3)} compact />
                                    </div>
                                </div>
                                <span class="trust-ranking-metric trust-ranking-metric-right">
                                    <span class="trust-ranking-metric-value">{formatPublicNumber(server.memberCount, locale.value)}</span>
                                    <span class="trust-ranking-metric-icon-wrap">
                                        <i class="material-symbols-outlined">groups</i>
                                    </span>
                                </span>
                                <span class="trust-ranking-metric trust-ranking-metric-right">
                                    <span class="trust-ranking-metric-value">
                                        {server.approximateOnlineCount !== null ? formatPublicNumber(server.approximateOnlineCount, locale.value) : "—"}
                                    </span>
                                    <span class="trust-ranking-metric-icon-wrap">
                                        <i class="material-symbols-outlined">visibility</i>
                                    </span>
                                </span>
                                <span class="trust-ranking-metric trust-ranking-metric-right">
                                    <span class="trust-ranking-metric-value">{formatPublicNumber(server.currentVoiceCount, locale.value)}</span>
                                    <span class="trust-ranking-metric-icon-wrap">
                                        <i class="material-symbols-outlined">settings_voice</i>
                                    </span>
                                </span>
                            </router-link>
                        ))}
                    </div>
                </div>

                <div class="trust-section-heading" data-reveal="section-heading">
                    <i class="material-symbols-outlined">reviews</i>
                    <div>
                        <span class="landing-kicker">{i18n("trust.testimonials.kicker")}</span>
                        <h2>{i18n("trust.testimonials.title")}</h2>
                    </div>
                </div>

                <div class="trust-testimonial-grid">
                    {data.value.testimonials.map((testimonial) => (
                        <TrustTestimonialCard
                            testimonial={testimonial}
                            server={testimonial.guildId ? serverById.value.get(testimonial.guildId) ?? null : null}
                        />
                    ))}
                </div>

                <PublicFooter />
            </div>
        );
    },
});
